import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plane, Eye, EyeOff, ArrowRight, Building2, ShieldCheck, Mail, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function SignUpPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [form, setForm] = useState({
    company_name: '',
    name: '',
    email: '',
    password: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const handleSendOtp = async () => {
    if (!form.email) {
      toast.error('Please enter your email first');
      return;
    }
    setSendingOtp(true);
    try {
      await api.post('/auth/send-signup-otp', { email: form.email });
      setOtpSent(true);
      toast.success('Verification code sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setVerifyingOtp(true);
    try {
      await api.post('/auth/verify-signup-otp', { email: form.email, otp });
      setOtpVerified(true);
      toast.success('Email verified!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.name || !form.email || !form.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!otpVerified) {
      toast.error('Please verify your email first');
      return;
    }
    setLoading(true);
    try {
      const res = await signup(form);
      toast.success(res.message || 'Welcome! Your agency is ready.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="signup-page">
      {/* Left: Hero Image */}
      <div
        className="hidden md:flex relative items-end overflow-hidden"
        style={{
          backgroundImage: `url(${process.env.REACT_APP_HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="relative z-10 p-10 pb-14 text-white space-y-5 max-w-lg">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold leading-tight tracking-tight">
            Your travel agency, powered by AI
          </h2>
          <div className="space-y-3">
            {[
              { n: '1', title: 'Verify your email', desc: 'Quick OTP verification for security' },
              { n: '2', title: 'Set up your agency', desc: 'Get a clean, dedicated CRM dashboard' },
              { n: '3', title: 'Start capturing leads', desc: 'AI auto-extracts from WhatsApp & Email' },
            ].map(step => (
              <div key={step.n} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg gradient-orange flex items-center justify-center flex-shrink-0 shadow-lg shadow-voyage-orange/30">
                  <span className="text-white font-heading font-bold text-sm">{step.n}</span>
                </div>
                <div>
                  <p className="font-medium text-sm">{step.title}</p>
                  <p className="text-xs text-white/70">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Signup Form */}
      <div className="flex items-center justify-center p-8 md:p-12 bg-background">
        <div className="w-full max-w-md space-y-6 animate-fade-in">
          {/* Logo */}
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              <div className="w-11 h-11 rounded-xl gradient-blue flex items-center justify-center shadow-lg shadow-voyage-blue/25">
                <Plane className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-2xl font-bold tracking-tight text-foreground">
                VoyageOS
              </span>
            </div>
            <div className="pt-3">
              <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
                Create your agency
              </h1>
              <p className="text-muted-foreground text-sm mt-1.5 leading-relaxed">
                Verify your email, then set up your CRM in seconds
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
            {/* Email + OTP Verification */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
              <div className="flex gap-2">
                <Input
                  id="email"
                  type="email"
                  placeholder="you@agency.com"
                  value={form.email}
                  onChange={(e) => { update('email', e.target.value); if (otpSent) { setOtpSent(false); setOtpVerified(false); setOtp(''); } }}
                  disabled={otpVerified}
                  className={`h-12 flex-1 bg-card border-border focus-visible:ring-2 focus-visible:ring-voyage-blue rounded-lg ${otpVerified ? 'border-emerald-300 bg-emerald-50/50' : ''}`}
                  data-testid="signup-email-input"
                />
                {!otpVerified && (
                  <Button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={sendingOtp || !form.email}
                    className="h-12 px-4 gradient-orange hover:opacity-90 text-white text-xs whitespace-nowrap"
                    data-testid="send-otp-btn"
                  >
                    {sendingOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : otpSent ? 'Resend' : 'Send OTP'}
                  </Button>
                )}
                {otpVerified && (
                  <div className="h-12 px-3 flex items-center gap-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-600">
                    <ShieldCheck className="w-4 h-4" />
                    <span className="text-xs font-semibold">Verified</span>
                  </div>
                )}
              </div>
            </div>

            {/* OTP Input (shown after sending) */}
            {otpSent && !otpVerified && (
              <div className="space-y-2 p-3 rounded-lg bg-voyage-blue/5 border border-voyage-blue/20 animate-fade-in">
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-voyage-blue" /> Enter 6-digit verification code
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    maxLength={6}
                    placeholder="000000"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="h-11 text-center text-lg font-mono tracking-[0.5em] bg-card border-border focus-visible:ring-2 focus-visible:ring-voyage-blue rounded-lg flex-1"
                    data-testid="otp-input"
                  />
                  <Button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={verifyingOtp || otp.length !== 6}
                    className="h-11 px-5 gradient-blue hover:opacity-90 text-white"
                    data-testid="verify-otp-btn"
                  >
                    {verifyingOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Check your inbox (and spam folder). Code expires in 10 minutes.</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="company_name" className="text-sm font-medium">
                Agency / Company Name *
              </Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-voyage-blue" />
                <Input
                  id="company_name"
                  placeholder="e.g., Wanderlust Travels"
                  value={form.company_name}
                  onChange={(e) => update('company_name', e.target.value)}
                  className="h-12 pl-10 bg-card border-border focus-visible:ring-2 focus-visible:ring-voyage-blue rounded-lg"
                  data-testid="signup-company-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">Your Full Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Rajesh Kumar"
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  className="h-12 bg-card border-border focus-visible:ring-2 focus-visible:ring-voyage-blue rounded-lg"
                  data-testid="signup-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+91 9876543210"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  className="h-12 bg-card border-border focus-visible:ring-2 focus-visible:ring-voyage-blue rounded-lg"
                  data-testid="signup-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min 6 characters"
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                  className="h-12 pr-10 bg-card border-border focus-visible:ring-2 focus-visible:ring-voyage-blue rounded-lg"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !otpVerified}
              className={`w-full h-12 font-medium rounded-lg transition-all duration-200 shadow-lg ${
                otpVerified
                  ? 'gradient-blue hover:opacity-90 text-white shadow-voyage-blue/20'
                  : 'bg-muted text-muted-foreground cursor-not-allowed shadow-none'
              }`}
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  {otpVerified ? 'Create My Agency' : 'Verify Email to Continue'} <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground text-center">
            You'll get a <span className="font-semibold text-voyage-orange">15-day free trial</span> with full access.
          </p>

          <div className="pt-3 border-t border-border">
            <p className="text-sm text-center text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-voyage-blue hover:underline font-semibold" data-testid="goto-login-link">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
